import {
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowLeft, ArrowSquareOut } from '@phosphor-icons/react';
import React, { useEffect } from 'react';
import { useStoreThirdPartyIntegration } from '../../../store/storeThirdPartyIntegration';
import {
  INTEGRATION_CATEGORY_LABELS,
  type IntegrationCategory,
} from '../../ThirdPartyIntegration/categories';
import ProviderIconBadge from '../../ThirdPartyIntegration/common/ProviderIconBadge';
import type {
  IntegrationProviderConfig,
  ProviderIconComponent,
} from '../../ThirdPartyIntegration/types';
import AddConnectionPanel from './AddConnectionPanel';
import ConnectionListPanel from './ConnectionListPanel';

interface IntegrationDetailViewProps {
  config: IntegrationProviderConfig;
  isDark: boolean;
  onBack: () => void;
}

const IntegrationDetailView: React.FC<IntegrationDetailViewProps> = ({
  config,
  isDark,
  onBack,
}) => {
  const fetchConnections = useStoreThirdPartyIntegration(
    state => state.fetchConnections,
  );
  const connections = useStoreThirdPartyIntegration(state =>
    state.getConnections(config.id),
  );

  useEffect(() => {
    fetchConnections(config.id);
  }, [config.id, fetchConnections]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onBack();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onBack]);

  const rawIcon = config.icon;
  const IconComponent: ProviderIconComponent | null =
    rawIcon && typeof rawIcon !== 'string'
      ? (rawIcon as ProviderIconComponent)
      : null;
  const brandColor = config.brandColor ?? (isDark ? '#7F56D9' : '#6941C6');
  const titleColor = isDark ? '#F9FAFB' : '#101828';
  const subtitleColor = isDark ? '#98A2B3' : '#667085';
  const headerBg = isDark ? '#0C111D' : '#FFFFFF';
  const dividerColor = isDark ? 'rgba(71, 84, 103, 0.4)' : '#EAECF0';
  const useCustomCard = Boolean(config.ConnectionCard);

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 99,
          backgroundColor: headerBg,
          borderBottom: `1px solid ${dividerColor}`,
          px: 3,
          py: 2,
        }}
      >
        <Button
          onClick={onBack}
          aria-label='Back to integrations list'
          startIcon={<ArrowLeft size={16} weight='bold' />}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 500,
            color: subtitleColor,
            px: 1,
            mb: 1,
            '&:hover': {
              backgroundColor: isDark
                ? 'rgba(71, 84, 103, 0.2)'
                : 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Back to integrations
        </Button>
        <Stack direction='row' alignItems='flex-start' spacing={2}>
          <ProviderIconBadge
            IconComponent={IconComponent}
            brandColor={brandColor}
            size={48}
            isDark={isDark}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: '20px', fontWeight: 600, color: titleColor }}
            >
              {config.name}
            </Typography>
            {config.description && (
              <Typography
                sx={{
                  fontSize: '13px',
                  color: subtitleColor,
                  mt: 0.5,
                  lineHeight: 1.5,
                  maxWidth: 640,
                }}
              >
                {config.description}
              </Typography>
            )}
            <Stack
              direction='row'
              alignItems='center'
              spacing={1.5}
              flexWrap='wrap'
              sx={{ mt: 1.5 }}
            >
              {config.category?.map(category => (
                <Chip
                  key={category}
                  size='small'
                  label={
                    INTEGRATION_CATEGORY_LABELS[category as IntegrationCategory]
                  }
                  sx={{
                    height: 22,
                    fontSize: '11px',
                    backgroundColor: isDark
                      ? 'rgba(71, 84, 103, 0.20)'
                      : '#F2F4F7',
                    color: titleColor,
                  }}
                />
              ))}
              {config.docsUrl && (
                <Link
                  href={config.docsUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  underline='hover'
                  sx={{
                    fontSize: '12px',
                    color: brandColor,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  Provider docs
                  <ArrowSquareOut size={12} weight='bold' />
                </Link>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          maxWidth: 720,
        }}
      >
        {useCustomCard && config.ConnectionCard ? (
          <config.ConnectionCard
            provider={config.id}
            isConnected={connections.length > 0}
          />
        ) : (
          <>
            <ConnectionListPanel config={config} isDark={isDark} />
            {connections.length > 0 && (
              <Divider sx={{ borderColor: dividerColor }} />
            )}
            <AddConnectionPanel
              config={config}
              isDark={isDark}
              hasExistingConnections={connections.length > 0}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default IntegrationDetailView;
